import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const mechanismDomains = [
  {
    name: '过程域',
    desc: '记录课堂完整发生过程，涵盖教学设计、师生互动与情境推进',
    count: '17',
    countUnit: '点',
    color: 'primary',
    icon: 'ri-route-line',
    items: ['教学设计', '情境导入', '师生互动', '知识展开'],
  },
  {
    name: '价值域',
    desc: '验证教学目标、价值导向与知识传递的正确性与合理性',
    count: '3',
    countUnit: '点',
    color: 'accent',
    icon: 'ri-shield-check-line',
    items: ['目标对齐', '价值正确', '逻辑严谨'],
  },
  {
    name: '事实域',
    desc: '核实课堂中呈现的事实、数据与论据的真实性与准确性',
    count: '1',
    countUnit: '点',
    color: 'secondary',
    icon: 'ri-file-list-3-line',
    items: ['事实核验'],
  },
  {
    name: '效果域',
    desc: '评估学生实际收获、课堂参与度和目标达成效果',
    count: '3',
    countUnit: '点',
    color: 'accent',
    icon: 'ri-bar-chart-box-line',
    items: ['学生收获', '参与度', '目标达成'],
  },
];

export default function P35BackendDomainsNew({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-100/14 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-100/12 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-6 md:py-8">
        {/* 标题 */}
        <div ref={titleRef} className="text-center mb-6 md:mb-8">
          <span className="inline-block text-lg md:text-xl font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-2 transition-all duration-700">后台结构</span>
          <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-foreground-950 transition-all duration-1000 delay-100">
            四大机制域与<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">MECE全量对应</span>
          </h2>
          <p className="text-2xl md:text-3xl text-foreground-950 font-body mt-2 max-w-2xl mx-auto">
            按判定机制归集 · 与 24 观测点 MECE 全量对应
          </p>
        </div>

        {/* 四大机制域卡片 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-5 md:mb-6">
          {mechanismDomains.map((domain, i) => (
            <div
              key={i}
              className={`group p-5 md:p-6 rounded-xl border text-left transition-all duration-500 hover:scale-[1.02] hover:shadow-lg ${
                domain.color === 'primary'
                  ? 'bg-primary-50/50 backdrop-blur-md border-primary-200/30'
                  : domain.color === 'accent'
                    ? 'bg-accent-50/50 backdrop-blur-md border-accent-200/30'
                    : 'bg-secondary-50/50 backdrop-blur-md border-secondary-200/30'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className={`w-14 h-14 md:w-16 md:h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    domain.color === 'primary'
                      ? 'bg-primary-100 text-primary-600'
                      : domain.color === 'accent'
                        ? 'bg-accent-100 text-accent-600'
                        : 'bg-secondary-100 text-secondary-600'
                  }`}
                >
                  <i className={`${domain.icon} text-xl md:text-2xl`} />
                </div>
                <div>
                  <h4
                    className={`text-3xl md:text-4xl font-heading font-bold ${
                      domain.color === 'primary'
                        ? 'text-primary-700'
                        : domain.color === 'accent'
                          ? 'text-accent-700'
                          : 'text-secondary-700'
                    }`}
                  >
                    {domain.name}
                  </h4>
                  <div
                    className={`inline-flex items-baseline gap-1 px-2 py-0.5 rounded-md ${
                      domain.color === 'primary'
                        ? 'bg-primary-100'
                        : domain.color === 'accent'
                          ? 'bg-accent-100'
                          : 'bg-secondary-100'
                    }`}
                  >
                    <span
                      className={`text-2xl md:text-3xl font-heading font-bold ${
                        domain.color === 'primary'
                          ? 'text-primary-600'
                          : domain.color === 'accent'
                            ? 'text-accent-600'
                            : 'text-secondary-600'
                      }`}
                    >
                      {domain.count}
                    </span>
                    <span
                      className={`text-base md:text-lg font-body ${
                        domain.color === 'primary'
                          ? 'text-primary-500'
                          : domain.color === 'accent'
                            ? 'text-accent-500'
                            : 'text-secondary-500'
                      }`}
                    >
                      {domain.countUnit}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-lg md:text-xl text-foreground-950 font-body leading-relaxed mb-3">{domain.desc}</p>

              <div className="flex flex-wrap gap-1.5">
                {domain.items.map((item) => (
                  <span
                    key={item}
                    className={`inline-block px-2.5 py-1 rounded-md text-base md:text-lg font-body font-medium ${
                      domain.color === 'primary'
                        ? 'bg-primary-100 text-primary-700'
                        : domain.color === 'accent'
                          ? 'bg-accent-100 text-accent-700'
                          : 'bg-secondary-100 text-secondary-700'
                    }`}
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}